import { useState } from 'react'

import {
  useCreateBatch,
  useRecipes,
  useRecipeV2Summary,
  useRegisterSample,
  useAllSamples,
} from '@ctcells/api'
import { BATCH_NEW_RECIPE_PARAM, dataSourceFor, PATHS } from '@ctcells/core'
import { errorMessage } from '@ctcells/i18n'
import { useNavigate, useSearchParams } from 'react-router'

import { toRecipeListRow } from '../recipes/recipeModel.ts'
import { recipeVocabularyNote, type RecipeVocabularyNote } from '../recipes/recipeVocabulary.ts'
import { registerSampleVars } from '../samples/sampleModel.ts'
import { useScreenAccess } from '../screenAccess.ts'

import { NewBatchScreen } from './NewBatchScreen.tsx'
import { BatchNewLockControl } from './BatchNewLockControl.tsx'
import { newFormatDoc } from './batchRecipeDoc.ts'
import { newBatchRecipes } from './newBatchRecipes.ts'
import { dispositionHref } from './dispositionModel.ts'

/**
 * 새 작업 컨테이너 — `POST /batches` 까지 실제로 나간다(2026-09-20).
 *
 * 🔴 **갈리는 층은 `dataSourceFor()` 하나다.** demo 경로는 네트워크 훅을 **부르지 않고**,
 *    쓰기 콜백도 붙이지 않는다 — 목 화면의 버튼이 실서버로 나가면 안 된다.
 *    ⚠ 이 화면은 두 도메인을 쓴다(`batches` + `recipes` + `samples`). **셋 다 live 여야**
 *      실물 경로로 간다 — 하나라도 목이면 고른 값의 출처가 갈려 만들 수 없는 조합이 된다.
 *
 * 🔴 **낙관적 갱신이 없다.** `createCommand` 가 성공 후 하는 일은 무효화뿐이고, 화면은
 *    `createdBatchId` 를 **서버 응답에서** 받는다. 만들었다고 화면이 먼저 넘어가지 않는다.
 *
 * 🔴 **성공해도 화면을 뜨지 않는다.** 스테퍼 3단계가 「사전 점검」이라 만든 자리에서 바로
 *    점검한다 — 상세로 튕기면 사람이 다시 돌아와야 한다. 상세로 가는 버튼은 따로 둔다.
 *    (「시작」은 여기 없다 — 시작 창구는 상시 작업 줄 하나다.)
 *
 * 경계 다섯
 * | 로딩 | 목록 조회 `isPending` → 표의 `emptyLabel` 이 「불러오는 중」                  |
 * | 에러 | 조회 실패는 그 목록 자리에 **사유**(`notices`), 쓰기 실패는 `errorMessage()` 가 그 자리에 |
 * | 빈   | 레시피 0건이면 「확정된 레시피 없음」 + 「＋ 새 레시피」로 길을 연다           |
 * | 권한 | `useScreenAccess()` → 숨기지 않고 비활성 + 사유                                |
 * | 끊김 | 같은 `access.controlDisabled`. `gate()` 가 1순위로 막는다                      |
 */

/** 서버 최대 500 아래. 정렬 파라미터가 없어 페이지 이동이 무의미하다 */
const PAGE_LIMIT = 200

/**
 * 주소가 실어 온 사전 선택 레시피. 🔴 **주소를 읽는 곳은 컨테이너 하나다** — 화면은
 * 라우터를 모른다. 값이 없으면 `undefined` 로 흘려 화면이 예전과 똑같이 시작한다.
 */
/** 새 형식 레시피 문서만 요약 계산에 보낸다 — 옛 형식(모듈 3종 절)은 그 계산의 입력이 아니다 */
function usePreselectedRecipe(): string | undefined {
  const [params] = useSearchParams()
  const value = params.get(BATCH_NEW_RECIPE_PARAM)
  return value === null || value === '' ? undefined : value
}

export interface BatchNewContainerProps {
  /** 창 안에 들어갈 때 `modal` — 화면이 제목·「←」·취소를 안 그린다(`NewBatchScreen.frame`) */
  frame?: 'page' | 'modal' | undefined
}

export function BatchNewContainer({ frame }: BatchNewContainerProps = {}) {
  const live =
    dataSourceFor('batches') === 'live' &&
    dataSourceFor('recipes') === 'live' &&
    dataSourceFor('samples') === 'live'

  return live ? <BatchNewLive frame={frame} /> : <BatchNewDemo frame={frame} />
}

/** demo 는 쓰기 콜백을 **안 붙인다** — 화면이 「목 데이터에서는 보낼 수 없습니다」로 비활성한다 */
function BatchNewDemo({ frame }: BatchNewContainerProps) {
  const access = useScreenAccess()
  const navigate = useNavigate()
  const preselected = usePreselectedRecipe()
  return (
    <NewBatchScreen
      access={access}
      frame={frame}
      {...(preselected === undefined ? {} : { initialRecipeId: preselected })}
      onCancel={() => void navigate(PATHS.batches)}
    />
  )
}

function BatchNewLive({ frame }: BatchNewContainerProps) {
  const access = useScreenAccess()
  const navigate = useNavigate()
  const preselected = usePreselectedRecipe()

  const recipes = useRecipes({ limit: PAGE_LIMIT, status: 'committed' })
  const samples = useAllSamples({})
  const create = useCreateBatch()
  const register = useRegisterSample()

  /**
   * 🔴 **서버가 준 id 다.** 응답 전에는 `undefined` 이고, 그동안 「작업 열기」가 없다.
   *    `create.data` 를 바로 읽지 않는 이유는 무효화 뒤에도 그 값이 남아야 하기 때문이다.
   */
  const [batchId, setBatchId] = useState<string | undefined>(undefined)
  /** 서버가 저장한 이름(201 응답). 사람이 적은 이름과 대조한다 — 93번 요청 전에는 조립 이름이 온다 */
  const [batchName, setBatchName] = useState<string | null | undefined>(undefined)
  /** 화면에서 지금 고른 레시피 — 트레이 그림(시료 자리)을 그 문서로 묻는다. 고르기 자체는 화면이 쥔다 */
  const [pickedRecipeId, setPickedRecipeId] = useState<string | undefined>(preselected)
  /** [상세] 팝업이 연 레시피 — 고른 것과 따로 그 레시피 id 로 요약을 묻는다 */
  const [detailRecipeId, setDetailRecipeId] = useState<string | undefined>(undefined)

  /**
   * 🔴 **`committed` 만 고를 수 있다.** 서버 `committed_version()` 이 그 밖을 422 로 떨어뜨린다.
   *    질의로도 좁히고(`status=committed`) 여기서 한 번 더 거른다 — 서버가 무시해도 화면이 틀리지 않게.
   */
  /*
   * 🔴 **고를 수 있는 레시피는 committed 전부다** — 이름으로 거르지 않는다(eric 2026-10-29 「표준 시나리오 말고 다른 이름도 고를 수 있게」 ·
   *    10-14 의 「CTCells 표준 시나리오」 앞머리 거름은 걷었다 · `newBatchRecipes.ts`).
   *    ⊕ 2026-10-27 — 창이 **사람이 웰마다 고른 것**으로 전건일 때만 `assignments` 를 싣는다(`newBatchAssignments.ts` `pickWell` ·
   *    eric 「해당 칸 또는 웰 별로 하는 건 어때?」 · 「응 한번 해봐」). 10-21 ~ 10-26 의 «담은 순서대로» 자동 배정은 걷었다.
   */
  const committed = newBatchRecipes(recipes.data?.items ?? [])
  const recipeRows = committed.map(toRecipeListRow)
  /*
   * 고른 레시피의 문서(목록 항목의 `payload` — 따로 조회하지 않는다)와 서버 요약(`POST /recipes/summary` · DB 무접촉 계산 · 템플릿 상세와 같은 호출).
   * 시료 구역의 트레이 그림(시료 자리 P1~P4 · REF ↔ 웰)과 레시피 구역 아래 본문(읽기 전용)이 같은 두 값을 읽는다.
   * 🔴 새 형식 문서만 보낸다 — 옛 형식은 그 계산의 입력이 아니다. 못 물으면 그림은 빈 칸 + 사유다(자리를 지어내지 않는다)
   */
  const pickedRecipe = committed.find((recipe) => recipe.recipe_id === pickedRecipeId)
  const pickedDoc = newFormatDoc(pickedRecipe?.payload)
  const pickedSummary = useRecipeV2Summary({
    doc: pickedDoc,
    cacheId:
      pickedRecipe === undefined
        ? 'none'
        : `${pickedRecipe.recipe_id}@${String(pickedRecipe.version)}`,
  })
  /*
   * [상세] 팝업의 레시피(고르지 않은 것도) — 같은 요약 호출을 그 레시피 id 로. 고른 레시피와 같으면 같은 캐시를 쓴다(`<id>@<version>`)
   */
  const detailRecipe = committed.find((recipe) => recipe.recipe_id === detailRecipeId)
  const detailDoc = newFormatDoc(detailRecipe?.payload)
  const detailSummary = useRecipeV2Summary({
    doc: detailDoc,
    cacheId:
      detailRecipe === undefined
        ? 'none'
        : `${detailRecipe.recipe_id}@${String(detailRecipe.version)}`,
  })
  /** 스텝 어휘 대조 — 표 대조일 뿐 판정이 아니다(`RecipeVocabularyReport`). 버튼을 막지 않는다 */
  const vocabularyNotes: Record<string, RecipeVocabularyNote> = {}
  for (const recipe of committed) {
    const note = recipeVocabularyNote(recipe.payload)
    if (note !== undefined) vocabularyNotes[recipe.recipe_id] = note
  }

  /**
   * 🔴 **미배정 시료만.** 판정 축은 `batch_id` 하나다(`unassignable_samples`).
   *    `== null` 로 본다 — 서버는 키를 주고 값만 `null` 이다.
   */
  const free = (samples.data?.items ?? []).filter((sample) => sample.batch_id == null)

  /**
   * 🔴 **못 본 것을 「0건」으로 접지 않는다.** `?? []` 는 화면을 살리려는 관대함이지
   *    「없다」는 판정이 아니다 — 그 «이유»는 여기서 따로 나른다.
   *
   * `GET /samples` 는 operator/admin 한정이라(`samples.py:81 READ_ROLES`) viewer 에게 403 이고,
   * 그때 `isPending` 도 `false` 라 고르개가 **아무 말 없이 「미배정 시료 없음」**이 됐다.
   */
  const noticeOf = (query: { isError: boolean; error: unknown }): string | undefined =>
    query.isError ? errorMessage(query.error as Parameters<typeof errorMessage>[0]) : undefined

  return (
    <NewBatchScreen
      access={access}
      lockControl={
        frame === 'modal' && !access.lockHeld ? <BatchNewLockControl access={access} /> : undefined
      }
      available={free}
      assignedSamples={(samples.data?.items ?? []).filter((sample) => sample.batch_id != null)}
      onInspectAssigned={(id) => void navigate(dispositionHref(PATHS.batches, id))}
      /* 「새 시료」 칸의 제안 — 같은 응답의 시료 전부(배정 여부 무관 · 2026-10-01 회의). 새 요청이 아니다 */
      hintSamples={samples.data?.items ?? []}
      notices={{ samples: noticeOf(samples), recipes: noticeOf(recipes) }}
      createdBatchId={batchId}
      createdBatchName={batchName}
      creating={create.isPending}
      frame={frame}
      {...(preselected === undefined ? {} : { initialRecipeId: preselected })}
      loading={recipes.isPending || samples.isPending}
      recipes={recipeRows}
      onRecipeChange={setPickedRecipeId}
      recipeDoc={
        pickedDoc === undefined
          ? undefined
          : {
              loading: pickedSummary.isPending,
              doc: pickedDoc,
              summary: pickedSummary.data,
              notice: noticeOf(pickedSummary),
            }
      }
      onRecipeDetail={setDetailRecipeId}
      recipeDetail={
        detailRecipe === undefined
          ? undefined
          : {
              recipeId: detailRecipe.recipe_id,
              loading: detailDoc !== undefined && detailSummary.isPending,
              doc: detailDoc,
              summary: detailSummary.data,
              notice: noticeOf(detailSummary),
            }
      }
      vocabularyNotes={vocabularyNotes}
      registering={register.isPending}
      {...(create.error === null ? {} : { createError: errorMessage(create.error) })}
      {...(register.error === null ? {} : { registerError: errorMessage(register.error) })}
      onCancel={() => void navigate(PATHS.batches)}
      onCreate={(recipeId, sampleIds, name, assignments) => {
        create.mutate(
          {
            recipeId,
            sampleIds,
            ...(name === undefined ? {} : { name }),
            /* 시료 ↔ 웰 — 사람이 웰마다 고른 것으로 전건일 때만 준다(`pickWell` · 2026-10-27) */
            ...(assignments === undefined ? {} : { assignments }),
          },
          {
            onSuccess: (batch) => {
              /*
               * 🔴 창(모달)은 만들어지면 그 자리에서 닫고 작업 목록으로 간다(eric 2026-10-21 「작업 추가하면 창이 바로 닫혀야지. 왜 상세
               *    버튼이 있어?」) — 목록은 생성 명령의 무효화(`keys.batches.all()`)로 다시 받는다. 시료 추가 창이 등록되면 닫히는 것과 같은 꼴.
               *    그래서 창 안에는 「작업 열기」 · 「이미 만들었습니다」 상태가 서지 않는다(값을 넘기지 않는다).
               * ⚠ 서버가 이름을 버렸다는 알림(`batchNew.name.ignored`)은 창이 닫혀 보일 자리가 없다 — 목록 화면에 알림 길(토스트 · 상태 줄)이
               *   없어 지어내지 않았다(팀장 보고). 페이지 판(장비 관점 콘솔)은 종전대로 남아 그 알림 · 「작업 열기」를 보인다
               */
              if (frame === 'modal') {
                void navigate(PATHS.batches)
                return
              }
              setBatchId(batch.batch_id)
              setBatchName(batch.name)
            },
          },
        )
      }}
      onNewRecipe={() => void navigate(PATHS.recipeNew)}
      onOpenBatch={() => {
        if (batchId !== undefined) void navigate(`${PATHS.batches}/${batchId}`)
      }}
      onRegisterSample={(draft) => {
        /* 페이지 판(장비 관점 콘솔)의 「새 시료」 폼만 부른다 — 창 안에는 그 단추가 없다(eric 2026-10-27) */
        register.mutate(registerSampleVars(draft))
      }}
    />
  )
}
